import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Form, Table, Button, OverlayTrigger, Tooltip, Badge, Modal } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BiPencil, BiTrash } from 'react-icons/bi';
import { LoaderContext } from '../../../App';
import AssignSubscriptionService from '../../../core/service/AssignSubscriptionService';
import ThirdPartyClientService from '../../../core/service/ThirdPartyClientService';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import ThirdPartyClientModal from './ThirdPartyClientModal';

const ConfigureThirdParties = ({ t }) => {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const filterBy = () => true;

  useEffect(() => {
    if (companyId > 0) {
      loadClients();
    } else {
      setClients([]);
    }
  }, [companyId]);

  const loadClients = () => {
    setLoaderDisplay(true);
    ThirdPartyClientService.getClientsByCompany(companyId)
      .then((response) => {
        if (response.status === 200) {
          setClients(response.data || []);
        }
      })
      .catch((error) => {
        toast.error(t("ConfigureThirdParties.loadError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const handleSearch = (query) => {
    setIsLoading(true);
    AssignSubscriptionService.getCompanyList(query)
      .then(res => {
        setIsLoading(false);
        if (res.status !== 200) return;

        const options = res.data?.map((i) => ({
          id: i.companyId,
          legalName: i.legalName,
        }));
        setCompanies(options);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  const handleCreate = () => {
    setSelectedClientId(null);
    setShowModal(true);
  };

  const handleEdit = (clientId) => {
    setSelectedClientId(clientId);
    setShowModal(true);
  };

  const handleDelete = (clientId) => {
    setClientToDelete(clientId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (confirmed) => {
    if (!confirmed) {
      setShowDeleteModal(false);
      setClientToDelete(null);
      return;
    }

    if (!clientToDelete) {
      setShowDeleteModal(false);
      return;
    }

    setLoaderDisplay(true);
    ThirdPartyClientService.deleteClient(clientToDelete)
      .then((response) => {
        if (response.status === 200 || response.status === 204) {
          toast.success(t("ConfigureThirdParties.deleteSuccess.label"), {
            position: toast.POSITION.TOP_RIGHT,
          });
          loadClients();
        }
      })
      .catch((error) => {
        toast.error(t("ConfigureThirdParties.deleteError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setLoaderDisplay(false);
        setShowDeleteModal(false);
        setClientToDelete(null);
      });
  };

  const handleModalSuccess = (credentials) => {
    if (credentials) {
      setCreatedCredentials(credentials);
      setShowCredentialsModal(true);
    }
    loadClients();
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info(t("Copied.label") || "Copied", {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="ConfigureThirdParties" data-testid="configure-third-parties">
      <div className="List_header mb-3">
        <h5 className="MH">{t("ConfigureThirdParties.label")}</h5>
      </div>
      <div>
        <Form>
          <div className="row m-0 align-items-center">
            <div className="col-lg-4 p-0">
              <div className="p-2">
                <label className="mb-1">{t("company.label")} <span className='text-danger'>*</span></label>
                <AsyncTypeahead
                  filterBy={filterBy}
                  id="company"
                  isLoading={isLoading}
                  labelKey="legalName"
                  minLength={2}
                  onSearch={handleSearch}
                  onChange={(selected) => {
                    if (selected[0]) {
                      setCompanyId(selected[0].id);
                      setCompanyName(selected[0].legalName);
                    } else {
                      setCompanyId(0);
                      setCompanyName('');
                    }
                  }}
                  options={companies}
                  placeholder={t("Search.label")}
                  renderMenuItemChildren={(option, props) => (
                    <Fragment>
                      <span>{option.legalName}</span>
                    </Fragment>
                  )}
                />
              </div>
            </div>
          </div>
        </Form>
      </div>

      {companyId > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-end mb-3">
            <Button variant="primary" onClick={handleCreate} data-testid="create-button">
              {t("Create.label")}
            </Button>
          </div>

          {clients.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>{t("ConfigureThirdParties.table.thirdPartyName.label")}</th>
                  <th>{t("ConfigureThirdParties.table.clientId.label")}</th>
                  <th>{t("ConfigureThirdParties.table.allowedApis.label")}</th>
                  <th>{t("Enabled.label")}</th>
                  <th>{t("ConfigureThirdParties.table.lastUsedAt.label")}</th>
                  <th>{t("CreatedDate.label")}</th>
                  <th>{t("Actions.label")}</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.thirdPartyName}</td>
                    <td>{client.keycloakClientId || '-'}</td>
                    <td>
                      {client.allowedApis && client.allowedApis.length > 0
                        ? client.allowedApis.join(', ')
                        : '-'}
                    </td>
                    <td>
                      {client.enabled ? (
                        <Badge bg="success">{t("Yes.label")}</Badge>
                      ) : (
                        t("Disabled.label")
                      )}
                    </td>
                    <td>{formatDate(client.lastUsedAt)}</td>
                    <td>{formatDate(client.createdDate)}</td>
                    <td>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{t("Edit.label")}</Tooltip>}
                      >
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleEdit(client.id)}
                          data-testid={`edit-button-${client.id}`}
                          className="p-1"
                        >
                          <BiPencil />
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{t("Delete.label")}</Tooltip>}
                      >
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-1"
                          onClick={() => handleDelete(client.id)}
                          data-testid={`delete-button-${client.id}`}
                        >
                          <BiTrash />
                        </Button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center p-4">
              <p>{t("ConfigureThirdParties.noClients.label")}</p>
            </div>
          )}
        </div>
      )}

      <ThirdPartyClientModal
        show={showModal}
        onHide={() => setShowModal(false)}
        clientId={selectedClientId}
        companyId={companyId}
        companyName={companyName}
        onSuccess={handleModalSuccess}
      />

      {showDeleteModal && (
        <ConfirmationModal
          modalShow={true}
          messageText={t("ConfigureThirdParties.deleteModal.message.label")}
          callbackModal={handleDeleteConfirm}
          title={t("ConfigureThirdParties.deleteModal.title.label")}
          confirmBtn={t("Delete.label")}
          CancelBtn={t("Cancel.label")}
        />
      )}

      <Modal
        show={showCredentialsModal}
        onHide={() => {
          setShowCredentialsModal(false);
          setCreatedCredentials(null);
        }}
        centered
        data-testid="credentials-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("ConfigureThirdParties.credentialsModal.title.label")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">{t("ConfigureThirdParties.credentialsModal.message.label")}</p>
          <Form.Group className="mb-3">
            <Form.Label>{t("ConfigureThirdParties.credentialsModal.clientId.label")}</Form.Label>
            <div className="input-group">
              <Form.Control
                type="text"
                value={createdCredentials?.clientId || ''}
                readOnly
                data-testid="credentials-client-id"
              />
              <Button
                variant="outline-secondary"
                onClick={() => handleCopyToClipboard(createdCredentials?.clientId || '')}
                data-testid="copy-client-id-button"
              >
                {t("ConfigureThirdParties.credentialsModal.copy.label")}
              </Button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t("ConfigureThirdParties.credentialsModal.clientSecret.label")}</Form.Label>
            <div className="input-group">
              <Form.Control
                type="text"
                value={createdCredentials?.clientSecret || ''}
                readOnly
                data-testid="credentials-client-secret"
              />
              <Button
                variant="outline-secondary"
                onClick={() => handleCopyToClipboard(createdCredentials?.clientSecret || '')}
                data-testid="copy-client-secret-button"
              >
                {t("ConfigureThirdParties.credentialsModal.copy.label")}
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setShowCredentialsModal(false);
              setCreatedCredentials(null);
            }}
          >
            {t("Ok.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default withTranslation()(ConfigureThirdParties);
